/* pulse-drag-rail.js
 *
 * Click and drag a rail left or right with a mouse (Andre, 2026-09-02: "I want
 * all our product rails and cards carousels to move when you drag the mouse
 * left or right as well ... this doesn't apply to collection pages, just the
 * carousels under the product and on the home page and any other page we have
 * a carousel type section").
 *
 * ONE shared behaviour for every rail rather than a copy per section. The only
 * rail left out is `[data-pulse-rail]`, which carries its own drag inside
 * assets/pulse-product.js tied to that rail's go()/nearest() paging; binding it
 * twice would run two scroll writers against one element. It is skipped by an
 * explicit guard in bind(), not by absence from the selector, because the two
 * are easy to confuse: the PDP's OTHER rail, `.pdp-rail__track`, looks like the
 * same thing and has only arrows, so it does belong here.
 *
 * The collection product wall is excluded by construction, not by a guard: it
 * is a grid with no horizontal overflow, so the scrollWidth test below never
 * matches it. Filter chip strips, tab strips and the size chart table are left
 * out on purpose; they are controls, not carousels.
 *
 * Five things this has to get right, each of which breaks the feature on its
 * own:
 *
 *   1. SUSPEND SCROLL SNAP FOR THE DRAG. Every one of these tracks is
 *      `scroll-snap-type: x mandatory`. Writing scrollLeft on a mandatory-snap
 *      container makes the browser re-snap to the nearest snap point straight
 *      away, so the rail jumps card to card instead of following the mouse.
 *      Setting snap to none for the duration and restoring it on release gives
 *      a smooth drag AND a clean settle onto a card at the end.
 *   2. SUPPRESS THE CLICK. The cards are anchors. Without a capture-phase click
 *      handler that cancels after real movement, letting go of a drag navigates
 *      to whichever product is under the cursor.
 *   3. KILL THE NATIVE DRAG. Dragging across an <img> or an <a> starts the
 *      browser's own drag-and-drop with a ghost image.
 *   4. KILL TEXT SELECTION, or the drag paints a blue selection across every
 *      card name it crosses.
 *   5. MOUSE ONLY. Touch and pen already scroll these natively; taking their
 *      pointer events over is how a phone loses vertical page scroll.
 */
(function () {
  'use strict';

  /* Explicit opt-in first, so a new carousel only needs the attribute. The
     rest are the rails that already existed when this was written. */
  var SELECTOR = [
    '[data-drag-rail]',
    '.psr__track', //             home: the shop rail filmstrip
    '.best__cards', //            home: best sellers (phone carousel)
    '.pdp-gallery__track', //     PDP: the image gallery
    '.pdp-rail__track', //        PDP: other colours / other styles
    '.stories__row', //           PDP: the dance roundels
    '.pinv__rail-inner', //       innovation strip
  ].join(',');

  /* A drag that starts on a control is that control's drag, not the rail's.
     The shop rail's quick-add sheet puts a real <select> inside a track.

     <button> is deliberately NOT here. It was, and it silently killed the PDP
     dance roundels: every card in that rail IS a button, so every pointerdown
     landed on one and the drag was skipped. A button is a click target exactly
     like the product-card anchors, and the click suppression below already
     covers it. Only controls that consume a horizontal drag of their own
     belong in this list. */
  var CONTROLS = 'select,textarea,input:not([type="checkbox"]):not([type="radio"]),[contenteditable="true"]';

  /* Below this many pixels of travel it was a click, not a drag. Keeps a
     slightly shaky click on a product card working as a click. */
  var THRESHOLD = 5;

  var RAILS = [];
  var refreshTimer = 0;
  window.addEventListener('resize', function () {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(function () {
      RAILS.forEach(function (r) {
        r.__pulseDragRefresh();
      });
    }, 150);
  });

  function bind(rail) {
    if (!rail || rail.__pulseDrag) return;
    /* assets/pulse-product.js already drags this one, and pages it to dots and
       arrows on release. Two scroll writers on one element is a fight. */
    if (rail.hasAttribute('data-pulse-rail')) return;
    rail.__pulseDrag = true;

    var startX = 0;
    var startLeft = 0;
    var down = false;
    var moved = false;
    var snap = '';
    var behavior = '';

    function begin(e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      if (e.target.closest && e.target.closest(CONTROLS)) return;
      /* Nothing to drag: a rail that fits its own box is not a carousel today,
         even if it is one at another width. Checked per press, not once at
         load, because a resize changes the answer. */
      if (rail.scrollWidth - rail.clientWidth <= 8) return;
      down = true;
      moved = false;
      startX = e.clientX;
      startLeft = rail.scrollLeft;
    }

    function move(e) {
      if (!down) return;
      /* Trap 4 (WP4-C, 2026-09-06). Pointer capture only starts once the press has travelled
         THRESHOLD px, so a press that leaves the rail before then -- straight down, out of the
         box -- released its button somewhere this rail never heard about, and `down` stayed
         true. Move the mouse back over the rail with NO button held and it scrubbed. The button
         state on the event is the authority, not our own flag: if nothing is held, the gesture
         is over, so finish it properly (restore snap, drop is-dragging) rather than just
         returning, which would leave the rail suspended for the rest of the page's life. */
      if (e.buttons === 0) {
        end(e);
        /* end() only clears `moved` when the release lands outside the rail's box, because a
           release ON the rail is followed by a click the handler below must swallow. Here we
           are INSIDE the rail (this is a pointermove on it) but the button came up somewhere
           we never saw -- outside the browser window -- so no click is coming and a sticky
           `moved` would eat the customer's next real card click. */
        moved = false;
        return;
      }
      var dx = e.clientX - startX;
      if (!moved) {
        if (Math.abs(dx) < THRESHOLD) return;
        moved = true;
        /* Trap 1: snap has to go before the first scrollLeft write, or that
           write lands on a snap point instead of under the cursor. */
        snap = rail.style.scrollSnapType;
        rail.style.scrollSnapType = 'none';
        /* And trap 1b, same shape. `.fd__reel` sets `scroll-behavior: smooth`
           so its arrow buttons glide; under a drag that makes every scrollLeft
           write animate, and the rail eases along behind the cursor instead of
           tracking it (measured 2026-09-02: 40px of travel into a 200px drag).
           Smooth is right for programmatic paging and wrong for direct
           manipulation, so it is suspended per gesture rather than removed. */
        behavior = rail.style.scrollBehavior;
        rail.style.scrollBehavior = 'auto';
        rail.classList.add('is-dragging');
        if (rail.setPointerCapture) {
          try {
            rail.setPointerCapture(e.pointerId);
          } catch (err) {
            /* capture is a nicety; the drag still works without it */
          }
        }
      }
      rail.scrollLeft = startLeft - dx;
    }

    function end(e) {
      /* begin() only ever starts on a mouse, so only a mouse can finish. On a hybrid device a
         stray touch or pen pointerup -- now that this is bound on the window as well -- would
         otherwise terminate a live mouse drag mid-gesture. */
      if (e.pointerType !== 'mouse') return;
      if (!down) return;
      down = false;
      if (rail.releasePointerCapture && rail.hasPointerCapture && rail.hasPointerCapture(e.pointerId)) {
        rail.releasePointerCapture(e.pointerId);
      }
      if (!moved) return;
      rail.classList.remove('is-dragging');
      /* Restoring snap is what settles the rail onto a card. Deferred a frame
         so the release does not fight the last scrollLeft write. */
      window.requestAnimationFrame(function () {
        rail.style.scrollSnapType = snap;
        rail.style.scrollBehavior = behavior;
      });
      /* `moved` is cleared by the click handler below, which fires after this.
         Clearing it here would let the click through.
         WP4-C: unless the release landed OUTSIDE the rail's box, in which case no click can be
         generated on the rail at all and `moved` would stay true, swallowing the customer's
         NEXT genuine click on a card. Decided on the release coordinates rather than e.target,
         because pointer capture retargets the event to the rail either way. */
      var box = rail.getBoundingClientRect();
      var insideRail = e.clientX >= box.left && e.clientX <= box.right && e.clientY >= box.top && e.clientY <= box.bottom;
      if (!insideRail) moved = false;
    }

    rail.addEventListener('pointerdown', begin);
    rail.addEventListener('pointermove', move);
    rail.addEventListener('pointerup', end);
    rail.addEventListener('pointercancel', end);
    /* WP4-C: and on the window, so a release the rail never sees still ends the gesture. Both
       are bound on purpose; `end` returns immediately when `down` is already false, so the
       ordinary release-over-the-rail case runs the body exactly once. */
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);

    /* Trap 2. Capture phase, so it runs before the card's own handler and
       before the anchor's default navigation. */
    rail.addEventListener(
      'click',
      function (e) {
        if (!moved) return;
        moved = false;
        e.preventDefault();
        e.stopPropagation();
      },
      true
    );

    /* Trap 3. */
    rail.addEventListener('dragstart', function (e) {
      if (moved || down) e.preventDefault();
    });

    /* The grab cursor is a promise, so only make it where the rail can
       actually move. Several of these are carousels on a phone and plain
       grids on a desktop, so this is a per-width answer, not a per-rail one. */
    rail.__pulseDragRefresh = function () {
      rail.classList.toggle('is-draggable', rail.scrollWidth - rail.clientWidth > 8);
    };
    rail.__pulseDragRefresh();
    RAILS.push(rail);
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll(SELECTOR), bind);
    /* A section render can hand back the track itself rather than a wrapper. */
    if (scope.matches && scope.matches(SELECTOR)) bind(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan(document);
    });
  } else {
    scan(document);
  }

  /* Theme editor: a re-rendered section arrives with fresh, unbound nodes. */
  document.addEventListener('shopify:section:load', function (e) {
    scan(e.target);
  });
})();
